import React, {FC, useCallback, useMemo, useState} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../libs/shared/core/src/helpers/internationalization/react';
import {useDebounce} from '../../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {InboxModel} from '../../../libs/shared/core/src/models/api/inboxModel';
import {TeammateModel} from '../../../libs/shared/core/src/models/api/teammateModel';
import {buildTeammateDisplayName} from '../../../libs/shared/core/src/models/helpers/teammateModelHelpers';
import {AuditTrailPanelId} from '../../../libs/shared/core/src/store/panels/panelStateBase';
import {StyledStretchGridItem} from '../../../libs/shared/ui/src/components/grid/grid';
import {LeftPanel} from '../../../libs/shared/ui/src/components/leftPanel/leftPanel';
import {useUserTheme} from '../../../libs/shared/ui/src/theme/userThemeProvider';
import {ResizeSeparator} from '../../components/common/resizeSeparator';
import {useAppDispatch, useAppSelector} from '../../helpers/appReduxHelpers';
import {LocalPreferencesSizesEnum} from '../../models/api/localPreferencesModel';
import {updateSizeAction} from '../../store/data/localPreferences/localPreferencesActions';
import {getSizes} from '../../store/data/localPreferences/localPreferencesSelectors';
import {AuditTrailFiltersSelection} from '../../store/panels/audit_trail/auditTrailParameters';
import {AuditTrailCategoryElement, AuditTrailFilterSection} from './auditTrailsFilterSection';

/*
 * Props.
 */

interface AuditTrailFiltersMenuProps {
  panelId: AuditTrailPanelId;
  activities: ReadonlyArray<AuditTrailCategoryElement>;
  inboxes: Array<InboxModel>;
  teammates: Array<TeammateModel>;
  filters: AuditTrailFiltersSelection;
  onFiltersChange: (selection: AuditTrailFiltersSelection) => void;
}

interface MenuSelection {
  type: keyof AuditTrailFiltersSelection;
  selection: ReadonlyArray<string> | undefined;
}

/*
 * Constants.
 */

const minSizePanelWidth = 100;
const maxSizePanelWidth = 300;

/*
 * Styles.
 */

const StyledFiltersMenuGrid = styled.div`
  display: grid;
`;

/*
 * Component.
 */

export const AuditTrailFiltersMenu: FC<AuditTrailFiltersMenuProps> = (props) => {
  const {panelId, activities, inboxes, teammates, filters, onFiltersChange} = props;

  const dispatch = useAppDispatch();
  const theme = useUserTheme();

  const onSelect: (selection: MenuSelection) => void = useCallback(
    (selection) =>
      onFiltersChange({
        ...filters,
        [selection.type]: selection.selection,
      }),
    [filters, onFiltersChange],
  );

  /**
   * Handle resize P1 menu.
   */
  const sizes = useAppSelector(getSizes);
  const [sideWidth, setSideWidth] = useState(sizes[LocalPreferencesSizesEnum.AUDIT_MENU]);

  const onSideWidthChange: (arg: number) => void = (newWidth) => {
    const boundedSideWidth = Math.min(maxSizePanelWidth, Math.max(minSizePanelWidth, newWidth));
    setSideWidth(boundedSideWidth);
    saveSideWidthDebounced(boundedSideWidth);
  };

  const saveSideWidthDebounced = useDebounce((newWidth: number) => {
    dispatch(updateSizeAction(LocalPreferencesSizesEnum.AUDIT_MENU, newWidth));
  }, 1000);

  /**
   * Section title renderers.
   */
  const eventName = useCallback(() => <Trans id="audit-trail-menu-category-events">Events</Trans>, []);
  const teammatesName = useCallback(() => <Trans id="audit-trail-menu-category-teammates">Team</Trans>, []);
  const inboxesName = useCallback(
    () => <Trans id="audit-trail-menu-category-inboxes">Team inboxes</Trans>,
    [],
  );

  /**
   * Section items
   */
  const inboxElements = useMemo(() => inboxes.map((i) => ({id: `${i.id}`, name: () => i.name})), [inboxes]);
  const teammateElements = useMemo(
    () => teammates.map((t) => ({id: `${t.id}`, name: () => buildTeammateDisplayName(t)})),
    [teammates],
  );

  return (
    <StyledFiltersMenuGrid>
      <StyledStretchGridItem column={1} style={{width: sideWidth}}>
        <LeftPanel panelId={panelId} interactionId="audit_trail">
          <AuditTrailFilterSection
            name={eventName}
            elements={activities}
            preselection={filters.activityTypes}
            selectionChanged={(itemIds) => onSelect({type: 'activityTypes', selection: itemIds})}
          />
          <AuditTrailFilterSection
            name={inboxesName}
            elements={inboxElements}
            preselection={filters.inboxIds?.map(String)}
            selectionChanged={(itemIds) => onSelect({type: 'inboxIds', selection: itemIds})}
          />
          <AuditTrailFilterSection
            name={teammatesName}
            elements={teammateElements}
            preselection={filters.teammateIds?.map(String)}
            selectionChanged={(itemIds) => onSelect({type: 'teammateIds', selection: itemIds})}
          />
        </LeftPanel>
      </StyledStretchGridItem>
      <ResizeSeparator
        column={2}
        size={sideWidth}
        minSize={minSizePanelWidth}
        maxSize={maxSizePanelWidth}
        onSizeChange={onSideWidthChange}
        color={theme.leftBar.separator}
      />
    </StyledFiltersMenuGrid>
  );
};
